import { FC, Fragment, useMemo, useState } from "react";
import { Box, SimpleGrid, Stack, Text } from "@chakra-ui/react";

import { useDetailState } from "scenes/OfferDetail";
import { IMarginProps } from "src/types/types";
import { createSearchUrl } from "scenes/Search/createSearchUrl";
import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import SearchLink from "components/Chakra/SearchLink";
import {
  protectedArea,
  queryParams,
  QueryParamsProps,
} from "@ud/config/prodeje/config";
import { TFilter } from "scenes/Search/types";
import { getOfferType } from "scenes/OfferDetail/helper";

type ParamDetail = {
  title: string;
  value: string | number;
  isLink?: boolean;
  id: keyof TFilter;
  ids?: Array<string>;
};

type LinkProps = {
  id: keyof TFilter;
  optionId: string;
  value: string | number;
};

const checkValidParam = (value: string | number): boolean => {
  if (typeof value === "string") {
    if (value.startsWith("0")) {
      return false;
    }

    return true;
  } else if (typeof value === "number") {
    return value !== 0;
  }

  return false;
};

const capitalizeFirstLetter = (text?: string) => {
  if (!text || text?.includes("https")) {
    return text;
  }

  const letter = text.charAt(0).toUpperCase();

  return letter + text.slice(1);
};

const getProtectionOptionTitle = ({
  paramId,
  optionId,
  optionTitle,
}: {
  paramId: string;
  optionId?: string | null;
  optionTitle?: string;
}) => {
  if (
    paramId !== "protection" ||
    !optionId ||
    !optionTitle?.toLowerCase().includes("offer.parameter.")
  ) {
    return optionTitle ?? "";
  }

  return (
    protectedArea[optionId as keyof typeof protectedArea]?.translationId ??
    optionTitle
  );
};

const getFilter = ({
  foundQueryParams,
  optionId,
  offer,
  id,
}: {
  foundQueryParams: QueryParamsProps;
  optionId: string;
  offer: DetailData;
  id: keyof TFilter;
}) => {
  const findConfig = Object.entries(foundQueryParams?.value).find(
    ([entryKey, { translationId }]) => {
      if (entryKey === optionId) {
        return {
          translationId,
        };
      }
    },
  );

  const newFilter: Partial<TFilter> = {};

  newFilter.offerType = offer?.offerTypeId as TFilter["offerType"];

  if (findConfig?.[1].translationId.length) {
    // @ts-ignore TS version error
    newFilter[id] = foundQueryParams.isNotArray ? optionId : [optionId];
  }

  return {
    newFilter,
    translationId: findConfig?.[1].translationId,
  };
};

const KeyValueLink: FC<LinkProps> = ({ id, optionId, value }) => {
  const { offer } = useDetailState();

  if (!offer) {
    return null;
  }

  const isCond =
    // @ts-ignore TODO smazat az pavel ten shit opraví offerFunction
    id === "offerType" || id === "propertyType" || id === "offerFunction";
  const foundQueryParams = isCond
    ? undefined
    : queryParams.find((item) => item.id === id);

  if (!foundQueryParams && !isCond) {
    return renderText(value);
  }

  const filter: {
    newFilter: Partial<TFilter>;
    translationId: string | undefined;
  } = !foundQueryParams
    ? {
        newFilter: {
          propertyType:
            id === "propertyType"
              ? (optionId as TFilter["propertyType"])
              : undefined,
          offerType:
            // @ts-ignore TODO smazat az pavel ten shit opraví offerFunction
            id === "offerType" || id === "offerFunction"
              ? (optionId as TFilter["offerType"])
              : undefined,
        },
        translationId: value.toString(),
      }
    : getFilter({
        foundQueryParams,
        optionId,
        offer,
        id,
      });

  return (
    <SearchLink
      href={createSearchUrl({
        filter: {
          ...filter.newFilter,
          offerType: getOfferType(offer.offerTypeId),
        },
        location: undefined,
      })}
      display="inline"
    >
      <Text
        _hover={{ color: "ud-primary" }}
        transition="color 0.3s"
        fontWeight="bold"
        display="inline"
      >
        {capitalizeFirstLetter(filter.translationId)}
      </Text>
    </SearchLink>
  );
};

const renderText = (passValue: string | number) => (
  <Text fontWeight="bold">{capitalizeFirstLetter(passValue.toString())}</Text>
);

const renderItem = ({ title, value, ids, isLink, id }: ParamDetail) => {
  if (isLink && ids?.length && ids.length > 1) {
    return (
      <>
        <Text w="40%">{title}</Text>
        <Box wordBreak="break-all" w="60%">
          {ids?.map((optionId, index) => (
            <Fragment key={index}>
              <KeyValueLink optionId={optionId} id={id} value={value} />
              {index !== ids.length - 1 && <Text display="inline">,</Text>}
            </Fragment>
          ))}
        </Box>
      </>
    );
  }

  return (
    <>
      <Text w="40%">{title}</Text>
      {isLink && ids?.length ? (
        <KeyValueLink optionId={ids[0]} id={id} value={value} />
      ) : (
        renderText(value)
      )}
    </>
  );
};

const showItems = 16;

const KeyValueInfo: React.FC<IMarginProps> = ({ ...margins }) => {
  const { offer } = useDetailState();
  const [isShowMore, setIsShowMore] = useState(false);

  const offerMemo = useMemo(() => {
    if (!offer || !offer.parameters) {
      return null;
    }

    const paramsText: Array<ParamDetail> = [];

    for (const [id, value] of Object.entries(offer.parameters)) {
      if (!value || !("title" in value)) {
        continue;
      }

      if ("options" in value) {
        const columnValue: ParamDetail = {
          title: value.title,
          id: id as keyof TFilter,
          value: "",
        };
        columnValue.value = value.options
          .map(({ id: optionId, title }) =>
            getProtectionOptionTitle({
              paramId: id,
              optionId,
              optionTitle: title,
            }),
          )
          .join(", ");
        const optionIdArray = value.options.map(({ id: optionId }) => optionId);

        columnValue.ids = optionIdArray.filter(Boolean) as Array<string>;
        columnValue.isLink = "seo" in value;
        checkValidParam(columnValue.value) && paramsText.push(columnValue);
      } else if ("value" in value && "id" in value && "seo" in value) {
        checkValidParam(value.value) &&
          paramsText.push({
            title: value.title,
            value: value.value,
            isLink: true,
            id: id as keyof TFilter,
            ids: [value.id] as Array<string>,
          });
      } else if ("value" in value) {
        checkValidParam(value.value) &&
          paramsText.push({
            id: id as keyof TFilter,
            title: value.title,
            value: value.value,
            isLink: false,
          });
      }
    }

    return paramsText;
  }, [offer]);

  return (
    <Stack {...margins} spacing={10}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {offerMemo
          ?.slice(0, isShowMore ? offerMemo.length : showItems)
          ?.map((item, index) => (
            <Stack
              key={index}
              py="10px"
              flex={["1", "1", "0 0 48%"]}
              borderBottomWidth="1px"
              borderColor="ud-blue-100"
              direction="row"
              spacing={0}
            >
              {renderItem(item)}
            </Stack>
          ))}
      </SimpleGrid>
      {offerMemo && offerMemo?.length > showItems && !isShowMore && (
        <Stack align="center">
          <Text
            onClick={() => setIsShowMore((prev) => !prev)}
            cursor="pointer"
            color="ud-primary"
            textDecoration="underline"
          >
            Zobrazit více
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export default KeyValueInfo;
